// pages/Booking.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  MapPin, 
  CreditCard, 
  Shield, 
  Clock, 
  CheckCircle,
  Settings,
  Wind,
  Droplets,
  Tv,
  Refrigerator,
  Microwave,
  Battery,
  Fan,
  Thermometer,
  Drill,
  Scissors,
  Bath,
  Zap,
  Home,
  ShoppingCart,
  Plus,
  AlertCircle
} from 'lucide-react';
import { createBooking, updateUserAddress } from '../services/api';
import { useCart } from '../context/CartContext';
import '../styles/Booking.css';

const BookingPage = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearCart, calculateItemTotal } = useCart();
  const service = location.state?.service;
  const cartItems = location.state?.cartItems;
  const isCartBooking = location.state?.isCartBooking;

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addressData, setAddressData] = useState({
    fullName: user?.displayName || '',
    phone: user?.phoneNumber || '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    landmark: ''
  });
  const [bookingData, setBookingData] = useState({
    date: '',
    timeSlot: '',
    additionalNotes: ''
  });
  const [bookingItems, setBookingItems] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);

  useEffect(() => {
    if (!service && !cartItems) {
      navigate('/services');
      return;
    }
    
    if (!user) {
      navigate('/', { 
        state: { 
          message: 'Please login to book a service',
          showAuth: true 
        } 
      });
      return;
    }

    // Initialize booking items
    if (service && !isCartBooking) {
      if (service.pricingType === 'variants' && service.variants) {
        setBookingItems([{
          id: Date.now(),
          variant: service.variants[0],
          quantity: 1
        }]);
      } else {
        setBookingItems([{
          id: Date.now(),
          quantity: service.quantity || 1,
          selectedBHK: service.selectedBHK,
          selectedVariant: service.selectedVariant
        }]);
      }
    }
  }, [service, cartItems, user, navigate, isCartBooking]);

  // Update available slots when date changes
  useEffect(() => {
    if (bookingData.date) {
      const slots = getAvailableTimeSlots(bookingData.date);
      setAvailableSlots(slots);
      // Clear selected slot if it's no longer available
      if (bookingData.timeSlot && !slots.includes(bookingData.timeSlot)) {
        setBookingData(prev => ({ ...prev, timeSlot: '' }));
      }
    }
  }, [bookingData.date]);

  const handleAddressChange = (field, value) => {
    setAddressData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBookingChange = (field, value) => {
    setBookingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddBookingItem = () => {
    if (service && service.pricingType === 'variants' && service.variants) {
      setBookingItems(prev => [...prev, {
        id: Date.now(),
        variant: service.variants[0],
        quantity: 1
      }]);
    }
  };

  const handleRemoveBookingItem = (itemId) => {
    if (bookingItems.length > 1) {
      setBookingItems(prev => prev.filter(item => item.id !== itemId));
    }
  };

  const handleUpdateBookingItem = (itemId, field, value) => {
    setBookingItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const calculateBookingItemPrice = (item) => {
    if (!service) return 0;
    
    if (service.pricingType === 'variants' && item.variant) {
      return item.variant.price * (item.quantity || 1);
    } else if (service.pricingType === 'bhk-based' && service.bhkPrices) {
      return service.bhkPrices[item.selectedBHK || '1BHK'] || service.basePrice;
    } else if (service.pricingType === 'per-unit' && service.priceMultiplier) {
      return service.priceMultiplier * (item.quantity || 1);
    }
    
    return service.price || service.basePrice || 0;
  };

  const validateAddress = () => {
    // Added 'state' to required fields
    const required = ['fullName', 'phone', 'address', 'city', 'state', 'pincode'];
    const missingFields = required.filter(field => !addressData[field]?.trim());
    
    if (missingFields.length > 0) {
      const fieldNames = {
        fullName: 'Full Name',
        phone: 'Phone Number',
        address: 'Address',
        city: 'City',
        state: 'State',
        pincode: 'Pincode'
      };
      setError(`Please fill in: ${missingFields.map(f => fieldNames[f]).join(', ')}`);
      return false;
    }
    
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(addressData.phone)) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    
    const pincodeRegex = /^\d{6}$/;
    if (!pincodeRegex.test(addressData.pincode)) {
      setError('Please enter a valid 6-digit pincode');
      return false;
    }
    
    setError('');
    return true;
  };

  const validateBooking = () => {
    if (!bookingData.date) {
      setError('Please select a service date');
      return false;
    }
    
    if (!bookingData.timeSlot) {
      setError('Please select a time slot');
      return false;
    }
    
    const selectedDate = new Date(bookingData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      setError('Please select a future date for the service');
      return false;
    }

    // Additional validation: Check if selected slot is at least 2 hours from now if today
    if (selectedDate.toDateString() === new Date().toDateString()) {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      
      // Parse selected slot start time
      const startTimeStr = bookingData.timeSlot.split(' - ')[0];
      const [time, period] = startTimeStr.split(' ');
      let [hours, minutes] = time.split(':').map(Number);
      
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      
      const slotTime = hours * 60 + minutes;
      const currentTime = currentHour * 60 + currentMinute;
      const twoHoursInMinutes = 120;
      
      if (slotTime - currentTime < twoHoursInMinutes) {
        setError('Please select a time slot at least 2 hours from now for same-day bookings');
        return false;
      }
    }
    
    setError('');
    return true;
  };

  const handleNextStep = async () => {
    if (currentStep === 1) {
      if (!validateAddress()) {
        return;
      }
      
      try {
        await updateUserAddress(addressData);
        setCurrentStep(2);
      } catch (error) {
        console.error('Error saving address:', error);
        setError('Error saving address. Please try again.');
      }
    } else if (currentStep === 2) {
      if (!validateBooking()) {
        return;
      }
      setCurrentStep(3);
    }
  };

  const handleConfirmBooking = async () => {
    if (!user) {
      setError('Please login to continue booking');
      navigate('/');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      if (isCartBooking && cartItems) {
        const bookingPromises = cartItems.map(async (item) => {
          const bookingDetails = {
            service: item.name,
            serviceType: item.selectedVariant?.name || item.name,
            servicePrice: calculateItemTotal(item),
            date: bookingData.date,
            timeSlot: bookingData.timeSlot,
            address: {
              street: addressData.address,
              city: addressData.city,
              state: addressData.state,
              pincode: addressData.pincode,
              landmark: addressData.landmark || ''
            },
            notes: bookingData.additionalNotes
          };
          return await createBooking(bookingDetails);
        });

        const bookingResults = await Promise.all(bookingPromises);
        clearCart();
        
        navigate('/booking-success', { 
          state: { 
            bookings: bookingResults,
            cartItems: cartItems,
            isMultipleBooking: true
          } 
        });
      } else if (service && bookingItems.length > 0) {
        const bookingPromises = bookingItems.map(async (item) => {
          const itemPrice = calculateBookingItemPrice(item);
          const serviceType = item.variant ? 
            `${service.name} - ${item.variant.name}` : 
            service.selectedVariant?.name || service.name;
          
          const bookingDetails = {
            service: service.name,
            serviceType: serviceType,
            servicePrice: itemPrice,
            quantity: item.quantity || 1,
            date: bookingData.date,
            timeSlot: bookingData.timeSlot,
            address: {
              street: addressData.address,
              city: addressData.city,
              state: addressData.state,
              pincode: addressData.pincode,
              landmark: addressData.landmark || ''
            },
            notes: bookingData.additionalNotes
          };
          return await createBooking(bookingDetails);
        });

        const bookingResults = await Promise.all(bookingPromises);
        
        navigate('/booking-success', { 
          state: { 
            bookings: bookingResults,
            service: service,
            isMultipleBooking: bookingItems.length > 1
          } 
        });
      }
      
    } catch (error) {
      console.error('Error creating booking:', error);
      setError(error.message || 'Error creating booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Modified to accept selectedDate and filter based on current time + 2 hours
  const getAvailableTimeSlots = (selectedDate) => {
    // All possible slots from 9 AM to 7 PM (9:00 to 19:00)
    const allSlots = [
      '9:00 AM - 11:00 AM',
      '11:00 AM - 1:00 PM',
      '1:00 PM - 3:00 PM',
      '3:00 PM - 5:00 PM',
      '5:00 PM - 7:00 PM'
    ];
    
    if (!selectedDate) return allSlots;
    
    const today = new Date();
    const selected = new Date(selectedDate);
    
    // Reset time parts for accurate date comparison
    today.setHours(0, 0, 0, 0);
    selected.setHours(0, 0, 0, 0);
    
    // If selected date is today, filter slots based on current time + 2 hours
    if (selected.getTime() === today.getTime()) {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentTimeInMinutes = (currentHour * 60) + currentMinute;
      const minTimeInMinutes = currentTimeInMinutes + 120; // Add 2 hours (120 minutes)
      
      return allSlots.filter(slot => {
        const startTimeStr = slot.split(' - ')[0];
        const [time, period] = startTimeStr.split(' ');
        let [hours, minutes] = time.split(':').map(Number);
        
        // Convert to 24-hour format
        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        
        const slotStartTimeInMinutes = (hours * 60) + minutes;
        return slotStartTimeInMinutes >= minTimeInMinutes;
      });
    }
    
    // For future dates, return all slots
    return allSlots;
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getServiceIcon = (serviceName) => {
    const iconProps = { size: 48 };
    
    const icons = {
      'House Cleaning': <Home {...iconProps} />,
      'Essential Home Cleaning': <Home {...iconProps} />,
      'Complete Kitchen Cleaning': <Home {...iconProps} />,
      'Kitchen Cleaning': <Home {...iconProps} />,
      'Bathroom Cleaning': <Bath {...iconProps} />,
      'Sofa Cleaning': <Home {...iconProps} />,
      'Carpet Cleaning': <Home {...iconProps} />,
      'Water Tank Cleaning': <Droplets {...iconProps} />,
      'AC Service': <Wind {...iconProps} />,
      'Split AC Jet Service': <Wind {...iconProps} />,
      'Split AC Dual Service': <Wind {...iconProps} />,
      'Split AC Triple Service': <Wind {...iconProps} />,
      'Split AC Quad Service': <Wind {...iconProps} />,
      'Window AC Jet Service': <Wind {...iconProps} />,
      'Window AC Dual Service': <Wind {...iconProps} />,
      'Window AC Triple Service': <Wind {...iconProps} />,
      'Window AC Quad Service': <Wind {...iconProps} />,
      'AC Repair': <Wind {...iconProps} />,
      'Split AC Installation': <Wind {...iconProps} />,
      'Split AC Uninstall': <Wind {...iconProps} />,
      'Split AC Uninstallation + Installation + Service': <Wind {...iconProps} />,
      'Window AC Installation': <Wind {...iconProps} />,
      'Split AC Install + Service Combo': <Wind {...iconProps} />,
      'Refrigerator Repair': <Refrigerator {...iconProps} />,
      'Washing Machine Repair': <Settings {...iconProps} />,
      'Washing Machine Top Load Service': <Settings {...iconProps} />,
      'Washing Machine Front Load Service': <Settings {...iconProps} />,
      'Washing Machine Semi Automatic Service': <Settings {...iconProps} />,
      'Washing Machine Installation': <Settings {...iconProps} />,
      'Microwave Oven Repair': <Microwave {...iconProps} />,
      'Hob/Stove Repair': <Settings {...iconProps} />,
      '3 Burner Hob Service': <Settings {...iconProps} />,
      '4 Burner Hob Service': <Settings {...iconProps} />,
      '5 Burner Hob Service': <Settings {...iconProps} />,
      'Kitchen Chimney Repair': <Settings {...iconProps} />,
      'Kitchen Chimney Service': <Settings {...iconProps} />,
      'Kitchen Chimney Installation': <Settings {...iconProps} />,
      'Geyser Repair': <Thermometer {...iconProps} />,
      'Geyser Installation': <Thermometer {...iconProps} />,
      'Geyser Service': <Thermometer {...iconProps} />,
      'RO/Water Purifier Repair': <Droplets {...iconProps} />,
      'RO/Water Purifier Installation': <Droplets {...iconProps} />,
      'RO/Water Purifier Service': <Droplets {...iconProps} />,
      'Air Cooler Repair': <Wind {...iconProps} />,
      'Fan Repair': <Fan {...iconProps} />,
      '1 Fan Installation': <Fan {...iconProps} />,
      '2 Fan Installation': <Fan {...iconProps} />,
      '3 Fan Installation': <Fan {...iconProps} />,
      '4 Fan Installation': <Fan {...iconProps} />,
      'LED TV Repair': <Tv {...iconProps} />,
      '32 inch TV Installation': <Tv {...iconProps} />,
      '43 inch TV Installation': <Tv {...iconProps} />,
      '64 inch TV Installation': <Tv {...iconProps} />,
      '75 inch TV Installation': <Tv {...iconProps} />,
      'Inverter Repair': <Battery {...iconProps} />,
      'Electrician Service': <Zap {...iconProps} />,
      'Plumber Service': <Droplets {...iconProps} />,
      'Carpenter Service': <Drill {...iconProps} />,
      'Men Saloon': <Scissors {...iconProps} />,
      'Women Saloon': <Scissors {...iconProps} />
    };
    
    return icons[serviceName] || <Settings {...iconProps} />;
  };

  const getTotalAmount = () => {
    if (isCartBooking && cartItems) {
      return cartItems.reduce((total, item) => total + calculateItemTotal(item), 0);
    }
    
    if (bookingItems.length > 0) {
      return bookingItems.reduce((total, item) => total + calculateBookingItemPrice(item), 0);
    }
    
    return service?.price || 0;
  };

  if (!service && !cartItems) {
    return <div>Loading...</div>;
  }

  return (
    <div className="booking-page">
      <div className="booking-header">
        <div className="container">
          <button className="back-button" onClick={() => navigate('/services')}>
            <ArrowLeft size={20} />
            Back to Services
          </button>
          <h1>Book Your Service</h1>
          <div className="booking-steps">
            <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
              <span>1</span>
              Address
            </div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
              <span>2</span>
              Schedule
            </div>
            <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
              <span>3</span>
              Payment
            </div>
          </div>
        </div>
      </div>

      <div className="booking-content">
        <div className="container">
          <div className="booking-layout">
            {/* Service Summary */}
            <div className="service-summary">
              {isCartBooking && cartItems ? (
                <div className="cart-summary">
                  <div className="cart-header">
                    <ShoppingCart size={24} />
                    <h3>Cart Items ({cartItems.length})</h3>
                  </div>
                  {cartItems.map((item, index) => (
                    <div key={index} className="cart-item-summary">
                      <div className="service-info">
                        <div className="service-icon">
                          {getServiceIcon(item.name)}
                        </div>
                        <div>
                          <h4>{item.name}</h4>
                          <div className="item-details">
                            <span>Qty: {item.quantity}</span>
                            <span>₹{calculateItemTotal(item)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : service && service.pricingType === 'variants' && service.variants ? (
                <div className="booking-items-manager">
                  <div className="service-header-info">
                    <div className="service-icon">
                      {getServiceIcon(service.name)}
                    </div>
                    <div>
                      <h3>{service.name}</h3>
                      <p className="service-subtitle">Select size and quantity for each item</p>
                    </div>
                  </div>
                  
                  <div className="booking-items-list">
                    {bookingItems.map((item, index) => (
                      <div key={item.id} className="booking-item-card">
                        <div className="booking-item-header">
                          <span className="item-number">Item {index + 1}</span>
                          {bookingItems.length > 1 && (
                            <button 
                              className="remove-item-btn"
                              onClick={() => handleRemoveBookingItem(item.id)}
                              aria-label="Remove item"
                            >
                              ×
                            </button>
                          )}
                        </div>
                        
                        <div className="booking-item-controls">
                          <div className="form-group">
                            <label>Size/Type</label>
                            <select
                              value={item.variant?.id || ''}
                              onChange={(e) => {
                                const selectedVariant = service.variants.find(v => v.id === e.target.value);
                                handleUpdateBookingItem(item.id, 'variant', selectedVariant);
                              }}
                              className="variant-select"
                            >
                              {service.variants.map(variant => (
                                <option key={variant.id} value={variant.id}>
                                  {variant.name} - ₹{variant.price}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="form-group">
                            <label>Quantity</label>
                            <div className="quantity-control">
                              <button
                                onClick={() => handleUpdateBookingItem(item.id, 'quantity', Math.max(1, (item.quantity || 1) - 1))}
                                className="qty-btn-small"
                              >
                                -
                              </button>
                              <span className="qty-display">{item.quantity || 1}</span>
                              <button
                                onClick={() => handleUpdateBookingItem(item.id, 'quantity', (item.quantity || 1) + 1)}
                                className="qty-btn-small"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        <div className="booking-item-price">
                          <span>Item Total:</span>
                          <span className="price-value">₹{calculateBookingItemPrice(item)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    className="add-another-item-btn"
                    onClick={handleAddBookingItem}
                  >
                    <Plus size={16} />
                    Add Another Item
                  </button>
                </div>
              ) : (
                <div className="service-info">
                  <div className="service-icon">
                    {getServiceIcon(service.name)}
                  </div>
                  <div>
                    <h3>{service.name}</h3>
                    <div className="service-details">
                      <div className="detail-item">
                        <Clock size={16} />
                        <span>{service.duration}</span>
                      </div>
                      <div className="detail-item">
                        <Shield size={16} />
                        <span>{service.warranty}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="price-breakdown">
                <div className="price-item">
                  <span>Service Charge</span>
                  <span>₹{getTotalAmount()}</span>
                </div>
                <div className="price-item">
                  <span>Taxes & Fees</span>
                  <span>₹0</span>
                </div>
                <div className="price-total">
                  <span>Total Amount</span>
                  <span>₹{getTotalAmount()}</span>
                </div>
              </div>
            </div>

            {/* Booking Form */}
            <div className="booking-form">
              {error && (
                <div className="error-alert">
                  <AlertCircle size={20} />
                  <span>{error}</span>
                </div>
              )}

              {currentStep === 1 && (
                <div className="form-step">
                  <div className="step-header">
                    <MapPin size={24} />
                    <div>
                      <h2>Service Address</h2>
                      <p>Where should our professional visit?</p>
                    </div>
                  </div>

                  <div className="form-grid">
                    <div className="form-group">
                      <label>Full Name *</label>
                      <input
                        type="text"
                        value={addressData.fullName}
                        onChange={(e) => handleAddressChange('fullName', e.target.value)}
                        placeholder="Enter your full name"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Phone Number *</label>
                      <input
                        type="tel"
                        value={addressData.phone}
                        onChange={(e) => handleAddressChange('phone', e.target.value)}
                        placeholder="Enter your phone number"
                        maxLength={10}
                      />
                    </div>
                    
                    <div className="form-group full-width">
                      <label>Complete Address *</label>
                      <textarea
                        value={addressData.address}
                        onChange={(e) => handleAddressChange('address', e.target.value)}
                        placeholder="House/Flat No., Building Name, Street Name"
                        rows="3"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>City *</label>
                      <input
                        type="text"
                        value={addressData.city}
                        onChange={(e) => handleAddressChange('city', e.target.value)}
                        placeholder="Enter your city"
                      />
                    </div>
                    
                    {/* Added State Field */}
                    <div className="form-group">
                      <label>State *</label>
                      <input
                        type="text"
                        value={addressData.state}
                        onChange={(e) => handleAddressChange('state', e.target.value)}
                        placeholder="Enter your state"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Pincode *</label>
                      <input
                        type="text"
                        value={addressData.pincode}
                        onChange={(e) => handleAddressChange('pincode', e.target.value)}
                        placeholder="Enter pincode"
                        maxLength={6}
                      />
                    </div>
                    
                    <div className="form-group full-width">
                      <label>Landmark (Optional)</label>
                      <input
                        type="text"
                        value={addressData.landmark}
                        onChange={(e) => handleAddressChange('landmark', e.target.value)}
                        placeholder="Nearby landmark for easy location"
                      />
                    </div>
                  </div>

                  <button className="next-btn" onClick={handleNextStep}>
                    Continue to Schedule
                  </button>
                </div>
              )}

              {currentStep === 2 && (
                <div className="form-step">
                  <div className="step-header">
                    <Clock size={24} />
                    <div>
                      <h2>Schedule Service</h2>
                      <p>Choose your preferred date and time (9 AM - 7 PM)</p>
                      {bookingData.date && new Date(bookingData.date).toDateString() === new Date().toDateString() && (
                        <p className="slot-note" style={{fontSize: '0.85rem', color: '#096651', marginTop: '4px'}}>
                          * Slots are available from 2 hours after current time for same-day bookings
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="form-grid">
                    <div className="form-group">
                      <label>Preferred Date *</label>
                      <input
                        type="date"
                        value={bookingData.date}
                        onChange={(e) => handleBookingChange('date', e.target.value)}
                        min={getMinDate()}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Time Slot *</label>
                      <select
                        value={bookingData.timeSlot}
                        onChange={(e) => handleBookingChange('timeSlot', e.target.value)}
                        disabled={!bookingData.date || availableSlots.length === 0}
                      >
                        <option value="">
                          {!bookingData.date 
                            ? 'Select date first' 
                            : availableSlots.length === 0 
                              ? 'No slots available for today' 
                              : 'Select time slot'}
                        </option>
                        {availableSlots.map(slot => (
                          <option key={slot} value={slot}>{slot}</option>
                        ))}
                      </select>
                      {bookingData.date && availableSlots.length === 0 && (
                        <small style={{color: '#dc3545', marginTop: '4px', display: 'block'}}>
                          No slots available for today. Please select tomorrow or later.
                        </small>
                      )}
                    </div>
                    
                    <div className="form-group full-width">
                      <label>Special Instructions (Optional)</label>
                      <textarea
                        value={bookingData.additionalNotes}
                        onChange={(e) => handleBookingChange('additionalNotes', e.target.value)}
                        placeholder="Any specific requirements or instructions for the service professional"
                        rows="3"
                      />
                    </div>
                  </div>

                  <div className="step-buttons">
                    <button className="back-btn" onClick={() => setCurrentStep(1)}>
                      Back
                    </button>
                    <button 
                      className="next-btn" 
                      onClick={handleNextStep}
                      disabled={availableSlots.length === 0 && bookingData.date && new Date(bookingData.date).toDateString() === new Date().toDateString()}
                    >
                      Continue to Payment
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="form-step">
                  <div className="step-header">
                    <CreditCard size={24} />
                    <div>
                      <h2>Payment</h2>
                      <p>Confirm your booking and payment</p>
                    </div>
                  </div>

                  <div className="booking-summary">
                    <h3>Booking Summary</h3>
                    {isCartBooking && cartItems ? (
                      <>
                        {cartItems.map((item, index) => (
                          <div key={index} className="summary-item">
                            <span>{item.name} (x{item.quantity}):</span>
                            <span>₹{calculateItemTotal(item)}</span>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="summary-item">
                        <span>Service:</span>
                        <span>{service.name}</span>
                      </div>
                    )}
                    <div className="summary-item">
                      <span>Date:</span>
                      <span>{new Date(bookingData.date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                    <div className="summary-item">
                      <span>Time:</span>
                      <span>{bookingData.timeSlot}</span>
                    </div>
                    <div className="summary-item">
                      <span>Address:</span>
                      <span>{addressData.address}, {addressData.city}, {addressData.state} - {addressData.pincode}</span>
                    </div>
                    <div className="summary-item total">
                      <span>Total Amount:</span>
                      <span>₹{getTotalAmount()}</span>
                    </div>
                  </div>

                  <div className="payment-info">
                    <div className="payment-note">
                      <CheckCircle size={20} />
                      <span>Payment will be collected after service completion</span>
                    </div>
                  </div>

                  <div className="step-buttons">
                    <button className="back-btn" onClick={() => setCurrentStep(2)}>
                      Back
                    </button>
                    <button 
                      className="confirm-btn"
                      onClick={handleConfirmBooking}
                      disabled={loading}
                    >
                      {loading ? 'Confirming...' : 'Confirm Booking'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;